import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  SessionDetailResponse,
  CreateJobResponse,
  CancelJobResponse,
  AnswerQuestionResponse,
  SetSessionHiddenResponse,
} from "@/lib/types";

import { fetchSession, createJob, cancelJob, answerQuestion, setSessionHidden } from "@/lib/api";
import { useStore } from "@/lib/store";

type CreateJobInput = {
  message: string;
  threadKey?: string;
  mode?: "plan" | "build";
  modelId?: string;
  attachmentIds?: string[];
};

export function useSession(threadKey: string | null): UseQueryResult<SessionDetailResponse> {
  return useQuery({
    queryKey: ["session", threadKey],
    queryFn: () => fetchSession(threadKey!),
    enabled: !!threadKey,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const hasPending = query.state.data?.jobs.some(
        (j) =>
          j.status === "pending" || j.status === "processing" || j.status === "awaiting_response",
      );
      return hasPending ? 2000 : false;
    },
  });
}

export function useSetSessionHidden(
  threadKey: string | null,
): UseMutationResult<SetSessionHiddenResponse, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hidden: boolean) => setSessionHidden(threadKey!, hidden),
    onSuccess: (result) => {
      queryClient.setQueryData<SessionDetailResponse>(["session", threadKey], (current) =>
        current ? { ...current, hidden: result.hidden } : current,
      );
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
}

export function useCreateJob(): UseMutationResult<CreateJobResponse, Error, CreateJobInput> {
  const queryClient = useQueryClient();
  const selectSession = useStore((state) => state.selectSession);

  return useMutation({
    mutationFn: ({ message, threadKey, mode, modelId, attachmentIds }: CreateJobInput) =>
      createJob(message, threadKey, mode, modelId, attachmentIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      selectSession(data.job.threadKey);

      if (data.isReply) {
        queryClient.invalidateQueries({
          queryKey: ["session", data.job.threadKey],
        });
      }
    },
  });
}

export function useCancelJob(
  threadKey: string | null,
): UseMutationResult<CancelJobResponse, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      if (threadKey) {
        queryClient.invalidateQueries({ queryKey: ["session", threadKey] });
      }
    },
  });
}

type AnswerQuestionInput = {
  jobId: string;
  answers: string[][];
};

export function useAnswerQuestion(
  threadKey: string | null,
): UseMutationResult<AnswerQuestionResponse, Error, AnswerQuestionInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, answers }: AnswerQuestionInput) => answerQuestion(jobId, answers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      if (threadKey) {
        queryClient.invalidateQueries({ queryKey: ["session", threadKey] });
      }
    },
  });
}
